import { useEffect, useRef, useState } from "react";
import { EditFormDetails, Item, Option } from "../interfaces";
import { isObject } from "../../services/utils";
import { GenericObject } from "../../services/Price";
import { GridRowModel } from "@mui/x-data-grid-pro";
import { Checkbox, Theme } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useIntl } from "react-intl";
import { useFormikContext } from "formik";
import { useQuery } from "react-query";
import { useDebounce } from "../../hooks/debounce";
import { ChipTag, SX_SINGLE_LINE_TAGS } from "../../Alert/Filter/Chips/ChipTag";
import { getSelectFilterValues, reducePath } from "../utils";
import { CustomAutocomplete } from "../../components/Inputs/CustomAutocomplete";

import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const useStyles = makeStyles()((theme: Theme) => ({
  // Only the field's width: fill and outline come from the wrapper and the theme.
  field: {
    flex: "0 1 224px",
    // Size from the flex basis only, never from the tags: otherwise wide
    // chips grow the field (and its dialog) instead of being ellipsized.
    minWidth: 0,
    contain: "inline-size",
  },
}));

/**
 * This function allows to disable an option
 * if the option limit number has already been exceeded.
 */
const getOptionDisabled = (
  filter: EditFormDetails,
  field: any,
  option: Option
) => {
  if (filter.limitValuesSelected && field.value) {
    const isOptionSelected = field.value.find(
      (currOption: any) => currOption.id === option.id
    );
    if (isOptionSelected) {
      return false;
    }
    return field.value.length >= filter.limitValuesSelected ? true : false;
  }
  return false;
};

/**
 * Build API query params from the filter config and the current row.
 */
const setConfigParams = (
  filter: EditFormDetails,
  row: GridRowModel | undefined
) => {
  const params: GenericObject = {};

  if (filter.apiParams) {
    for (const [key, value] of Object.entries(filter.apiParams)) {
      if (isObject(value) && row) {
        const data = (value as any).path.split(".");
        params[key] = reducePath(row, data);
      } else if (!isObject(value)) {
        params[key] = value;
      }
    }
  }

  return params;
};

/**
 * Read the watched field values from formik and add them to params.
 * Skips values that are -1 (the "All" sentinel) so the API is called
 * without that filter rather than receiving an invalid ID.
 */
const addWatchFieldParams = (
  params: GenericObject,
  values: any,
  filter: EditFormDetails
) => {
  if (!filter.watchField || typeof filter.watchField === "string") return;

  for (const wf of filter.watchField) {
    const formValue = values[wf.watchFieldPath.replace(/\./g, "__")];
    const value = wf.paramsValue
      ? wf.paramsValue
          .split(".")
          .reduce((acc: any, k: string) => acc?.[k], formValue)
      : formValue;

    if (value != null && value !== -1) {
      params[wf.paramsKey] = value;
    }
  }
};

/**
 * `SelectMultipleValues` is similar to the HTML <select> tag with multiple=True.
 * Options are retrieved by the API.
 */
export const SelectMultipleValues = ({
  filter,
  field,
  meta,
  helpers,
  row,
  dynamicPath,
  limitTags = 2,
}: {
  filter: EditFormDetails;
  field: any;
  meta: any;
  helpers: any;
  classes: any;
  row?: GridRowModel;
  dynamicPath?: boolean;
  limitTags?: number;
}) => {
  const intl = useIntl();
  const { classes: basicsClasses } = useStyles();
  const { values } = useFormikContext<any>();
  const [inputSearchValue, setInputSearchValue] = useState<string>("");

  const params = setConfigParams(filter, row);
  if (dynamicPath) {
    addWatchFieldParams(params, values, filter);
  }

  const debounceSearchValue = useDebounce(inputSearchValue, 400);

  const query = useQuery(
    [filter.api, params, debounceSearchValue],
    () =>
      getSelectFilterValues<Item>(
        filter.api as string,
        params,
        debounceSearchValue
      ),
    {
      keepPreviousData: true,
      select: (data) =>
        data.results.map((option) => ({
          id: option.id,
          data: filter.dataKey ? option[filter.dataKey] : option.id,
          label: option[filter.labelKey as string],
        })),
    }
  );

  // When editing an existing row with an empty value, auto-select all options.
  // Only on edit (row exists), not on creation — avoids selecting stale options
  // before the user has chosen dependent fields like property.
  const didAutoSelect = useRef(false);
  useEffect(() => {
    if (
      filter.selectAllByDefault &&
      row &&
      !didAutoSelect.current &&
      query.data &&
      (!field.value || field.value.length === 0)
    ) {
      didAutoSelect.current = true;
      if (query.data.length > 0) {
        helpers.setValue(query.data);
      }
    }
  }, [filter.selectAllByDefault, row, query.data, field.value, helpers]);

  const handleChange = (newValues: Option[] | null) => {
    helpers.setValue(newValues);
  };

  /**
   * Returns the (translated) label of the option.
   */
  const getOptionLabel = (option: Option): Option["label"] => {
    let label = option.label;

    // If labelKey is defined, we dynamically get the label from this key
    if (filter.labelKey) {
      const optionLabelKey = option[filter.labelKey as keyof Option];
      label = optionLabelKey ? `${optionLabelKey}` : label;
    }

    return label || "";
  };

  return (
    <div className={basicsClasses.field}>
      <CustomAutocomplete
        multiple
        disableCloseOnSelect
        disabled={false}
        error={meta.touched && !!meta.error}
        filterOptions={(options) => options}
        fullWidth
        getOptionLabel={(option: Option) =>
          option.label || getOptionLabel(option)
        }
        getOptionDisabled={(option) => getOptionDisabled(filter, field, option)}
        helperText={meta.touched && meta.error}
        isOptionEqualToValue={(option: Option, value: Option | null) =>
          option.id === value?.id
        }
        inputValue={inputSearchValue}
        label={intl.formatMessage({ id: filter.label })}
        // -1 disables MUI's truncation: ChipTag caps the chips itself below
        limitTags={-1}
        loading={query.isLoading}
        maxSelected={filter.limitValuesSelected}
        options={query.data || []}
        onBlur={field.onBlur}
        onChange={(_, newValues) => handleChange(newValues)}
        onClick={(e) => {
          /* This one is to avoid the table re-rendering on the focus loss */
          e.stopPropagation();
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (
            reason !== "reset" &&
            reason !== "selectOption" &&
            reason !== "removeOption"
          ) {
            setInputSearchValue(newInputValue);
          }
        }}
        // Capped even while focused, so the field keeps its height and the
        // open list does not move.
        renderValue={(value, getItemProps) => (
          <ChipTag
            getTagProps={getItemProps}
            maxVisibleTags={limitTags}
            // Values restored from an edited row are raw API objects without
            // `label`: resolve it like the options do (labelKey fallback).
            value={value.map((option: Option) => ({
              ...option,
              label: option.label || getOptionLabel(option),
            }))}
          />
        )}
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.id}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              sx={{ mr: 1, p: 0 }}
              checked={selected}
              size="small"
            />
            {option.label}
          </li>
        )}
        size="small"
        sx={SX_SINGLE_LINE_TAGS}
        value={field.value || []}
      />
    </div>
  );
};
